import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { isProjectScope } from '../../../../../shared/issuesV2';
import { useCreateFossabotPR } from '../../../../hooks/api/fossabot/useCreateFossabotPR';
import { useFossabotPrJobStore } from '../../../../hooks/api/fossabot/useFossabotPrJobStore';
import { useFossabotPRStatus } from '../../../../hooks/api/fossabot/useFossabotPRStatus';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';

import { useFossabotFixOptions } from './useFossabotFixOptions';
import type { FossabotFixBoxStatus } from './FossabotFixBox';

export type FossabotPrFlow = {
  status: FossabotFixBoxStatus;
  vcsUrl: string | null;
  fossabotUrl: string | null;
  /** Creates a PR upgrading to the given fix version (one of useFossabotFixOptions). */
  onCreatePR: (version: string) => void;
  /** Why the status could not be read; set only with the 'error' status. */
  errorMessage?: string;
  /** Refetches the status query, for the error state's Try again button. */
  onRetry?: () => void;
};

/**
 * Drives the FossabotFixBox: reads the issue's PR status (polling while it
 * moves), creates a PR on demand, and persists the creation jobId so a reload
 * mid-creation keeps showing 'creating'.
 *
 * A failed status read surfaces as the 'error' status with the server's
 * message and a retry, rather than hiding the box. A failed poll that still
 * has a last known status keeps showing it instead of dropping to the error.
 *
 * Returns null when the box should not render: no issue loaded yet, outside
 * project scope (a fossabot PR targets exactly one repo), or while the status
 * query is still loading (the box has no loading state).
 *
 * @param {boolean} isConnected - Whether the repository is connected to fossabot;
 *   the status query only runs once it is.
 */
export const useFossabotPrFlow = (isConnected: boolean): FossabotPrFlow | null => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const { getJobId, setJobId } = useFossabotPrJobStore();
  const { options } = useFossabotFixOptions();

  const issueId = issue?.id ?? null;
  const projectLocator = isProjectScope(scope) ? scope.id : undefined;
  const shouldFetch = isConnected && projectLocator !== undefined;
  // Harmless once a real PR exists: the route resolves it by repository +
  // dependency, and the stored id expires on its own TTL.
  const jobId = issueId !== null ? getJobId(issueId, projectLocator) : undefined;

  const {
    data,
    isError,
    error,
    refetch,
  } = useFossabotPRStatus(issueId, { projectLocator, jobId }, shouldFetch);
  const { mutate: createPR } = useCreateFossabotPR();

  const onCreatePR = useCallback((version: string): void => {
    if (issueId === null) {
      return;
    }

    /*
      The API only knows the two fix kinds, so map the chosen version back to
      whichever one it is. The backend defaults to 'complete' when the field is
      omitted, which would be wrong for a partial fix.
    */
    const isComplete = options.some((option) => option.version === version && option.isComplete);
    createPR(
      { issueId, fix: isComplete ? 'complete' : 'partial', projectLocator },
      {
        onSuccess: (created) => {
          if (created.jobId !== null) {
            setJobId(issueId, projectLocator, created.jobId);
          }
        },
      },
    );
  }, [createPR, options, issueId, projectLocator, setJobId]);

  if (issueId === null || !shouldFetch) {
    return null;
  }

  if (isError && !data) {
    return {
      status: 'error',
      vcsUrl: null,
      fossabotUrl: null,
      onCreatePR,
      errorMessage: getErrorMessage(error, 'Could not check the pull request status.'),
      onRetry: () => {
        refetch();
      },
    };
  }

  if (!data) {
    return null;
  }

  return {
    status: data.status,
    vcsUrl: data.vcsUrl,
    fossabotUrl: data.fossabotUrl,
    onCreatePR,
  };
};
